import { useState, useRef } from 'react';
import { Camera, Copy, X, Plus, Sparkles, Loader2, Trash2, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'entree', label: 'Entree', color: '#059669' },
  { id: 'plat', label: 'Plat', color: '#C9A84C' },
  { id: 'viande', label: 'Viande', color: '#DC2626' },
  { id: 'poisson', label: 'Poisson', color: '#3B82F6' },
  { id: 'tapas', label: 'Tapas', color: '#F59E0B' },
  { id: 'dessert', label: 'Dessert', color: '#8B5CF6' },
  { id: 'suggestion', label: 'Suggestion', color: '#0891B2' },
];

const catColor = (c) => CATEGORIES.find(x => x.id === c)?.color || '#6B7280';

export default function ScanMenuModal({ onClose, onImport }) {
  const [mode, setMode] = useState('photo');
  const [texte, setTexte] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [coefficient, setCoefficient] = useState(3.5);
  const fileRef = useRef(null);

  const scanFile = async (file) => {
    setScanning(true);
    setResults(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/api/menu-du-jour/scan-photo`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok && data.plats) {
        setResults(data.plats.map(p => ({ ...p, prix_kg: '', poids: '', cout_matiere: '', prix_vente: '', unite: 'kg' })));
      } else {
        toast.error(data.detail || 'Erreur scan');
      }
    } catch { toast.error('Erreur connexion'); }
    setScanning(false);
  };

  const scanTexte = async () => {
    if (!texte.trim()) return;
    setScanning(true);
    setResults(null);
    try {
      const res = await fetch(`${API}/api/menu-du-jour/scan-texte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte }),
      });
      const data = await res.json();
      if (res.ok && data.plats) {
        setResults(data.plats.map(p => ({ ...p, prix_kg: '', poids: '', cout_matiere: '', prix_vente: '', unite: 'kg' })));
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur connexion'); }
    setScanning(false);
  };

  const recalc = (plat) => {
    const pk = parseFloat(plat.prix_kg) || 0;
    const poids = parseFloat(plat.poids) || 0;
    const cout = pk > 0 && poids > 0 ? Math.round(pk * poids * 100) / 100 : parseFloat(plat.cout_matiere) || 0;
    const prix = cout > 0 ? Math.ceil((cout * coefficient) / 0.5) * 0.5 : '';
    return { ...plat, cout_matiere: pk > 0 && poids > 0 ? String(cout) : plat.cout_matiere, prix_vente: prix || plat.prix_vente };
  };

  const update = (idx, field, val) => {
    setResults(prev => prev.map((p, i) => {
      if (i !== idx) return p;
      const updated = { ...p, [field]: val };
      if (['prix_kg', 'poids', 'cout_matiere'].includes(field)) return recalc(updated);
      return updated;
    }));
  };

  const remove = (idx) => setResults(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-3xl p-4 sm:p-6 space-y-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="scan-menu-modal">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
              <Sparkles size={18} className="text-[rgb(var(--lm-gold))]" />
              Scanner & Creer des plats
            </h2>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">Photo, PDF ou texte → L'IA cree vos fiches plats avec descriptions et prix</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]"><X size={18} /></button>
        </div>

        {/* Input Mode */}
        {!results && !scanning && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button onClick={() => setMode('photo')}
                className={`flex-1 py-3 rounded-xl border-2 text-center transition-all ${mode === 'photo' ? 'border-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold))]/5' : 'border-[rgb(var(--lm-border))]/30'}`}>
                <Camera size={20} className={`mx-auto mb-1 ${mode === 'photo' ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'}`} />
                <p className="text-xs font-medium text-[rgb(var(--lm-text))]">Photo / PDF</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">Carnet, menu imprime</p>
              </button>
              <button onClick={() => setMode('texte')}
                className={`flex-1 py-3 rounded-xl border-2 text-center transition-all ${mode === 'texte' ? 'border-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold))]/5' : 'border-[rgb(var(--lm-border))]/30'}`}>
                <Copy size={20} className={`mx-auto mb-1 ${mode === 'texte' ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'}`} />
                <p className="text-xs font-medium text-[rgb(var(--lm-text))]">Texte</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">Copier-coller une liste</p>
              </button>
            </div>

            {mode === 'photo' ? (
              <div onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[rgb(var(--lm-border))] rounded-2xl p-10 text-center cursor-pointer hover:border-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold))]/3 transition-all">
                <Camera size={40} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
                <p className="text-sm text-[rgb(var(--lm-text))]">Prenez en photo ou importez</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">JPG, PNG, PDF — L'IA lit meme l'ecriture manuscrite</p>
                <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment" className="hidden"
                  onChange={e => e.target.files?.[0] && scanFile(e.target.files[0])} data-testid="scan-file-input" />
              </div>
            ) : (
              <div className="space-y-3">
                <textarea value={texte} onChange={e => setTexte(e.target.value)}
                  placeholder={"Collez votre liste de plats...\n\nExemple :\nJoue de Porc au Banyuls patate douce\nPaleron de Boeuf basse temperature legumes\nNage de Saumon coco curry riz\nOs a moelle grille"}
                  rows={6}
                  className="w-full text-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-xl px-4 py-3 text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] focus:outline-none focus:border-[rgb(var(--lm-gold))] resize-none"
                  data-testid="scan-texte-input" />
                <Button onClick={scanTexte} disabled={!texte.trim()} size="sm"
                  className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 h-10 text-sm" data-testid="scan-texte-btn">
                  <Sparkles size={14} className="mr-2" /> Transformer avec l'IA
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {scanning && (
          <div className="text-center py-12">
            <Loader2 size={32} className="animate-spin text-[rgb(var(--lm-gold))] mx-auto mb-3" />
            <p className="text-sm text-[rgb(var(--lm-text))]">L'IA analyse vos plats...</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Nom, description gastronomique, categorie</p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Top bar */}
            <div className="flex items-center justify-between bg-[rgb(var(--lm-surface))] rounded-xl px-4 py-2.5">
              <span className="text-sm font-bold text-[rgb(var(--lm-text))]">{results.length} plats</span>
              <div className="flex items-center gap-3">
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))]">Coefficient</label>
                <Input type="number" step="0.1" value={coefficient} onChange={e => {
                  const c = parseFloat(e.target.value) || 3.5;
                  setCoefficient(c);
                  setResults(prev => prev.map(p => {
                    const cout = parseFloat(p.cout_matiere) || 0;
                    return { ...p, prix_vente: cout > 0 ? Math.ceil((cout * c) / 0.5) * 0.5 : p.prix_vente };
                  }));
                }}
                  className="w-16 text-sm h-8 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-center font-bold" />
              </div>
            </div>

            {/* Dish cards */}
            {results.map((plat, i) => (
              <div key={i} className="bg-white dark:bg-[rgb(var(--lm-card))] rounded-2xl border border-[rgb(var(--lm-border))]/30 overflow-hidden shadow-sm" data-testid={`scan-result-${i}`}>

                {/* Dish header */}
                <div className="px-4 pt-4 pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <select value={plat.categorie}
                          onChange={e => update(i, 'categorie', e.target.value)}
                          className="px-2 py-1 rounded-lg text-[11px] font-bold text-white border-0 outline-none cursor-pointer"
                          style={{ background: catColor(plat.categorie) }}
                          data-testid={`cat-select-${i}`}>
                          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                        <h3 className="text-base font-bold text-[rgb(var(--lm-text))] leading-tight">{plat.nom}</h3>
                      </div>
                      <p className="text-[11px] text-[rgb(var(--lm-text-secondary))] leading-relaxed">{plat.description}</p>
                    </div>
                    <button onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors shrink-0">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Pricing section */}
                <div className="bg-[rgb(var(--lm-surface))]/50 px-4 py-3 border-t border-[rgb(var(--lm-border))]/20">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

                    {/* Prix au kg/unite */}
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">
                        Prix / {plat.unite === 'piece' ? 'piece' : plat.unite === 'litre' ? 'litre' : 'kg'}
                      </label>
                      <div className="flex gap-1">
                        <Input type="number" step="0.01" value={plat.prix_kg}
                          onChange={e => update(i, 'prix_kg', e.target.value)}
                          placeholder="0.00" className="flex-1 text-sm h-9 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-center"
                          data-testid={`prix-kg-${i}`} />
                        <select value={plat.unite || 'kg'}
                          onChange={e => update(i, 'unite', e.target.value)}
                          className="text-[10px] h-9 px-1 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]"
                          data-testid={`unite-${i}`}>
                          <option value="kg">kg</option>
                          <option value="piece">pc</option>
                          <option value="litre">L</option>
                          <option value="portion">port.</option>
                        </select>
                      </div>
                    </div>

                    {/* Poids portion */}
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">
                        Portion ({plat.unite === 'piece' ? 'qte' : plat.unite === 'litre' ? 'cl' : 'g'})
                      </label>
                      <Input type="number" step="0.01" value={plat.poids}
                        onChange={e => update(i, 'poids', e.target.value)}
                        placeholder={plat.unite === 'kg' ? 'ex: 0.250' : 'ex: 1'}
                        className="w-full text-sm h-9 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-center"
                        data-testid={`poids-${i}`} />
                    </div>

                    {/* Cout matiere */}
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Cout matiere</label>
                      <div className="relative">
                        <Input type="number" step="0.01" value={plat.cout_matiere}
                          onChange={e => update(i, 'cout_matiere', e.target.value)}
                          placeholder="0.00"
                          className="w-full text-sm h-9 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-center font-semibold pr-6"
                          data-testid={`cout-${i}`} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[rgb(var(--lm-text-muted))]">€</span>
                      </div>
                    </div>

                    {/* Prix de vente */}
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Prix de vente</label>
                      <div className="relative">
                        <Input type="number" step="0.5" value={plat.prix_vente}
                          onChange={e => update(i, 'prix_vente', e.target.value)}
                          placeholder="0.00"
                          className="w-full text-sm h-9 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-gold))]/40 text-center font-bold text-[rgb(var(--lm-gold))] pr-6"
                          data-testid={`prix-vente-${i}`} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[rgb(var(--lm-gold))]">€</span>
                      </div>
                      {plat.cout_matiere && plat.prix_vente && parseFloat(plat.prix_vente) > 0 && (
                        <p className={`text-center text-[10px] font-bold mt-1 ${(parseFloat(plat.cout_matiere) / parseFloat(plat.prix_vente) * 100) > 33 ? 'text-red-500' : 'text-green-500'}`}>
                          Food Cost {(parseFloat(plat.cout_matiere) / parseFloat(plat.prix_vente) * 100).toFixed(0)}%
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button onClick={() => onImport(results)} size="sm"
                className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 h-10 text-sm font-bold" data-testid="import-scan-results">
                <Plus size={14} className="mr-2" /> Ajouter au menu
              </Button>
              <Button onClick={() => setResults(null)} size="sm" variant="outline"
                className="h-10 text-sm border-[rgb(var(--lm-border))] px-6">
                Rescanner
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
